import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TeeFormModal } from '@/components/ui/tee-form-modal';
import { TeeTextArea } from '@/components/ui/tee-textarea';
import { TeeSelectableChip } from '@/components/ui/tee-selectable-chip';
import { TeeDropDownList } from '@/components/ui/tee-dropdown-list';
import { TeeDropZone, type TeeDropZoneErrorDetail } from '@/components/ui/tee-drop-zone';
import TeeBadge from '@/components/ui/tee-badge';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeToast } from '@/components/ui/tee-toast';
import { useAssistant } from '@/contexts/AssistantContext';
import { useAuth } from '@/hooks/useAuth';
import { useSwitcherLocations } from '@/hooks/useFoundingPricing';
import { useIntakeWizard } from '@/hooks/useIntakeWizard';
import { CandidateConfirmStep } from './steps/CandidateConfirmStep';
import { IntakeLoadingStep } from './steps/IntakeLoadingStep';
import { SuggestedFixCard } from './steps/SuggestedFixCard';
import { ResolvedSuccessStep } from './steps/ResolvedSuccessStep';
import { EscalationConfirmationStep } from './steps/EscalationConfirmationStep';
import {
  INTAKE_ATTACHMENT_ACCEPT,
  INTAKE_ATTACHMENT_MAX_MB,
  INTAKE_PRODUCTS,
  INTAKE_REQUEST_TYPES,
  INTAKE_SEVERITIES,
  type IntakeProduct,
  type IntakeRequestType,
  type IntakeSeverity,
} from '@/types/intake';

export interface IntakePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const REQUEST_TYPE_LABEL_KEYS: Record<IntakeRequestType, string> = {
  issue: 'intake.request_type_issue',
  feature_request: 'intake.request_type_feature',
  feedback: 'intake.request_type_feedback',
};

const SEVERITY_LABEL_KEYS: Record<IntakeSeverity, string> = {
  regular: 'intake.severity_regular',
  critical: 'intake.severity_critical',
};

interface IntakeFieldErrors {
  whatHappened?: string;
  whosAffected?: string;
  locationId?: string;
}

export function IntakePanel({ isOpen, onClose }: IntakePanelProps) {
  const { t } = useTranslation();
  const { effectiveScope } = useAssistant();
  const { user } = useAuth();
  const { data: switcherLocations = [], isLoading: locationsLoading } =
    useSwitcherLocations();
  const wizard = useIntakeWizard();

  const [requestType, setRequestType] = useState<IntakeRequestType>('issue');
  const [product, setProduct] = useState<IntakeProduct>('box_os');
  const [locationId, setLocationId] = useState('');
  const [whatHappened, setWhatHappened] = useState('');
  const [whosAffected, setWhosAffected] = useState('');
  const [severity, setSeverity] = useState<IntakeSeverity>('regular');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [errors, setErrors] = useState<IntakeFieldErrors>({});
  const submitting = wizard.isBusy;

  // D-05/D-06: page-derived scope first, then the admin's home/primary
  // location, then the first switcher location.
  const defaultLocationId =
    effectiveScope?.locationId ??
    (effectiveScope?.type === 'location' ? effectiveScope.id : undefined) ??
    user?.locationIds?.[0] ??
    switcherLocations[0]?.id ??
    '';

  // Reset-on-open: every open re-reads the current page scope, matching the
  // retired feedback modal's reset-on-open pattern.
  useEffect(() => {
    if (!isOpen) return;
    setRequestType('issue');
    setProduct('box_os');
    setLocationId(defaultLocationId);
    setWhatHappened('');
    setWhosAffected('');
    setSeverity('regular');
    setAttachment(null);
    setAttachmentError(null);
    setErrors({});
    wizard.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Covers the switcher list arriving after open; never overwrites a value
  // the admin already changed.
  useEffect(() => {
    if (isOpen && !locationId && defaultLocationId) {
      setLocationId(defaultLocationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultLocationId, locationId]);

  const handleDropZoneError = (
    message: string,
    detail?: TeeDropZoneErrorDetail,
  ) => {
    if (!detail) {
      setAttachmentError(message);
      return;
    }
    setAttachmentError(
      t(
        detail.reason === 'size' ? 'intake.error_file_size' : 'intake.error_file_type',
        { name: detail.fileName, size: INTAKE_ATTACHMENT_MAX_MB },
      ),
    );
  };

  const handlePrimaryAction = async () => {
    const trimmedWhatHappened = whatHappened.trim();
    const trimmedWhosAffected = whosAffected.trim();

    const nextErrors: IntakeFieldErrors = {};
    if (!trimmedWhatHappened) {
      nextErrors.whatHappened = t('intake.error_required', {
        field: t('intake.what_happened_label'),
      });
    }
    if (!locationId) {
      nextErrors.locationId = t('intake.error_required', {
        field: t('intake.location_label'),
      });
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    wizard.submit({
      requestType,
      product,
      locationId,
      whatHappened: trimmedWhatHappened,
      whosAffected: trimmedWhosAffected || undefined,
      severity,
      attachment,
    });
  };

  // Per-step footer (409-UI-SPEC "Phase-Specific Flow Contract"): the form
  // step keeps TeeFormModal's default Submit/Cancel; every wizard step past
  // it either hides the footer, shows a terminal Done, or — on error —
  // Cancel + Retry. `footer={<></>}` (not undefined) hides the default pair.
  const footer =
    wizard.step === 'form'
      ? undefined
      : wizard.error
        ? (
            <>
              <TeeButton
                variant="secondary"
                size="s"
                text={t('common.cancel')}
                onClick={onClose}
                className="flex-1 md:flex-none md:w-auto md:min-w-[150px]"
              />
              <TeeButton
                variant="primary"
                size="s"
                text={t('common.retry')}
                onClick={wizard.retry}
                className="flex-1 md:flex-none md:w-auto md:min-w-[150px]"
              />
            </>
          )
        : wizard.step === 'resolved' || wizard.step === 'escalation_confirmation'
          ? (
              <TeeButton
                variant="primary"
                size="s"
                text={t('common.done')}
                onClick={onClose}
                className="flex-1 md:flex-none md:w-auto md:min-w-[150px]"
              />
            )
          : <></>;

  const renderStepBody = () => {
    switch (wizard.step) {
      case 'candidate_confirm':
        return (
          <CandidateConfirmStep
            candidates={wizard.candidates}
            onChoose={wizard.chooseCandidate}
            disabled={wizard.isBusy}
          />
        );
      case 'diagnostic_loading':
        return <IntakeLoadingStep text={t('intake.fixmatch.loading')} />;
      case 'fix_card':
        return wizard.fix ? (
          <SuggestedFixCard
            fix={wizard.fix}
            onAnswer={wizard.answerFix}
            disabled={wizard.isBusy}
          />
        ) : null;
      case 'resolved':
        return <ResolvedSuccessStep />;
      case 'escalation_loading':
        return <IntakeLoadingStep text={t('intake.escalation.loading')} />;
      case 'escalation_confirmation':
        return wizard.ticket ? (
          <EscalationConfirmationStep ticket={wizard.ticket} />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <TeeFormModal
      isOpen={isOpen}
      title={t('intake.panel_title')}
      onClose={onClose}
      primaryActionLabel={t('intake.submit')}
      isLoadingLabel={t('intake.submitting')}
      secondaryActionLabel={t('common.cancel')}
      onSecondaryAction={onClose}
      onPrimaryAction={handlePrimaryAction}
      isLoading={submitting}
      dismissOnBackdropClick={false}
      dismissOnEscape={false}
      variant="panel"
      footer={footer}
    >
      {wizard.step !== 'form' && wizard.error && (
        <TeeToast
          type="error"
          message={t('intake.escalation.error')}
          className="mb-4"
        />
      )}
      {wizard.step === 'form' ? (
      <div className="flex flex-col gap-6">
        {/* a) Request Type */}
        <div className="flex flex-col gap-1.5">
          <p className="text-[14px] font-medium text-[var(--text-content-title)]">
            {t('intake.request_type_question')}
          </p>
          <div className="flex flex-wrap gap-2">
            {INTAKE_REQUEST_TYPES.map((rt) => (
              <TeeSelectableChip
                key={rt}
                label={t(REQUEST_TYPE_LABEL_KEYS[rt])}
                selected={requestType === rt}
                disabled={submitting}
                onClick={() => setRequestType(rt)}
                className="min-h-[44px] md:min-h-0"
              />
            ))}
          </div>
        </div>

        {/* b) Product + Location row */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <TeeDropDownList
            label={t('intake.product_label')}
            options={INTAKE_PRODUCTS.map((p) => ({
              label: t(`intake.product_${p}`),
              value: p,
            }))}
            value={product}
            disabled={submitting}
            onChange={(v) => setProduct(v as IntakeProduct)}
          />
          <TeeDropDownList
            label={t('intake.location_label')}
            placeholder={t('intake.location_placeholder')}
            options={switcherLocations.map((l) => ({
              label: l.name,
              value: l.id,
            }))}
            value={locationId}
            disabled={locationsLoading || submitting}
            onChange={(v) => setLocationId(String(v))}
            hint={errors.locationId ?? t('intake.location_hint')}
          />
        </div>

        {/* c) What happened? */}
        <TeeTextArea
          label={t('intake.what_happened_label')}
          placeholder={t('intake.what_happened_placeholder')}
          rows={4}
          value={whatHappened}
          destructive={!!errors.whatHappened}
          hint={errors.whatHappened}
          disabled={submitting}
          onChange={(e) => setWhatHappened(e.target.value)}
        />

        {/* d) Who's affected? */}
        <TeeTextArea
          label={t('intake.whos_affected_label')}
          placeholder={t('intake.whos_affected_placeholder')}
          rows={3}
          value={whosAffected}
          destructive={!!errors.whosAffected}
          hint={errors.whosAffected}
          disabled={submitting}
          onChange={(e) => setWhosAffected(e.target.value)}
        />

        {/* e) Severity */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <p className="text-[14px] font-medium text-[var(--text-content-title)]">
              {t('intake.severity_label')}
            </p>
            {severity === 'critical' && (
              <TeeBadge type="red" size="s" label={t('intake.severity_critical')} />
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {INTAKE_SEVERITIES.map((s) => (
              <TeeSelectableChip
                key={s}
                label={t(SEVERITY_LABEL_KEYS[s])}
                selected={severity === s}
                disabled={submitting}
                onClick={() => setSeverity(s)}
                className="min-h-[44px] md:min-h-0"
              />
            ))}
          </div>
          <p className="text-[14px] text-[var(--text-content-sub-title)]">
            {t('intake.severity_helper')}
          </p>
        </div>

        {/* f) Attachment */}
        <div className="flex flex-col gap-1.5">
          <TeeDropZone
            label={t('intake.attachment_label')}
            accept={INTAKE_ATTACHMENT_ACCEPT}
            maxSizeMb={INTAKE_ATTACHMENT_MAX_MB}
            file={attachment}
            onFileSelected={(f) => {
              setAttachment(f);
              setAttachmentError(null);
            }}
            onFileCleared={() => setAttachment(null)}
            onError={handleDropZoneError}
            idleText={t('intake.attachment_idle')}
            browseText={t('intake.attachment_browse')}
            removeText={t('intake.attachment_remove')}
            disabled={submitting}
          />
          {attachmentError && (
            <p role="alert" className="text-[14px] text-[var(--color-maroon)]">
              {attachmentError}
            </p>
          )}
        </div>
      </div>
      ) : !wizard.error ? (
        renderStepBody()
      ) : null}
    </TeeFormModal>
  );
}

export default IntakePanel;
